import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import MobileHeader from './MobileHeader';
import Spinner from './Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCode,
  faSearch,
  faFilter,
  faDownload,
  faEye,
  faEdit,
  faPlus,
  faBuilding,
  faUser,
  faEnvelope,
  faCalendar,
  faDollarSign,
  faChartLine,
  faSort,
  faSortUp,
  faSortDown,
  faCheckCircle,
  faTimesCircle,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';

const AdminAffiliateCodesTracking = () => {
  const { currentUser } = useContext(AuthContext);
  const { theme } = useTheme();
  const [affiliateCodes, setAffiliateCodes] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAffiliate, setSelectedAffiliate] = useState('ALL');
  const [sortField, setSortField] = useState('CreatedDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showStatsCards, setShowStatsCards] = useState(true);

  useEffect(() => {
    if (currentUser?.isAdmin) {
      loadData();
    }
  }, [currentUser]);

  // Load affiliate codes when affiliates are loaded
  useEffect(() => {
    if (affiliates.length > 0) {
      loadAffiliateCodes();
    }
  }, [affiliates]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load affiliates list first
      const affiliatesResponse = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getAffiliates',
          payload: { userId: currentUser.userId }
        })
      });

      const affiliatesResult = await affiliatesResponse.json();
      console.log('Affiliates API Response:', affiliatesResult);
      if (Array.isArray(affiliatesResult)) {
        setAffiliates(affiliatesResult);
        console.log('Affiliates loaded:', affiliatesResult.length);
      } else if (affiliatesResult && affiliatesResult.success) {
        setAffiliates(affiliatesResult.data || []);
        console.log('Affiliates loaded:', affiliatesResult.data?.length || 0);
      } else {
        console.error('Failed to load affiliates:', affiliatesResult?.error || 'Unexpected response format');
        setAffiliates([]);
      }

      // Load all affiliate codes after affiliates are loaded
      // (loadAffiliateCodes will be called after affiliates state is updated)
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAffiliateCodes = async (affiliateId = null) => {
    try {
      // If no specific affiliate is selected, get codes for all affiliates
      if (!affiliateId) {
        // We need to get codes for all affiliates - let's call for each affiliate
        let allCodes = [];
        for (const affiliate of affiliates) {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                action: 'getAffiliateCodes',
                payload: { 
                  userId: currentUser.userId,
                  affiliateId: affiliate.AffiliateId
                }
              })
            });

            const result = await response.json();
            if (Array.isArray(result)) {
              allCodes = [...allCodes, ...result];
            }
          } catch (error) {
            console.error(`Error loading codes for affiliate ${affiliate.AffiliateId}:`, error);
          }
        }
        console.log('All affiliate codes loaded:', allCodes.length);
        setAffiliateCodes(allCodes);
        return;
      }

      // Load codes for specific affiliate
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getAffiliateCodes',
          payload: { 
            userId: currentUser.userId,
            affiliateId: affiliateId
          }
        })
      });

      const result = await response.json();
      console.log('Affiliate Codes API Response:', result);
      if (Array.isArray(result)) {
        setAffiliateCodes(result);
        console.log('Affiliate codes loaded:', result.length);
      } else if (result && result.success) {
        setAffiliateCodes(result.data || []);
        console.log('Affiliate codes loaded:', result.data?.length || 0);
      } else {
        console.error('Failed to load affiliate codes:', result?.error || 'Unexpected response format');
        setAffiliateCodes([]);
      }
    } catch (error) {
      console.error('Error loading affiliate codes:', error);
    }
  };

  const handleAffiliateFilter = (affiliateId) => {
    setSelectedAffiliate(affiliateId);
    if (affiliateId === 'ALL') {
      loadAffiliateCodes();
    } else {
      loadAffiliateCodes(parseInt(affiliateId));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDirection === 'asc' ? faSortUp : faSortDown;
  };

  const filteredAndSortedCodes = affiliateCodes
    .filter(code => {
      const matchesSearch = searchQuery === '' || 
        code.ReferralCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        code.SalespersonName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        code.SalespersonEmail?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === 'string') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const totalCodes = affiliateCodes.length;
  const activeCodes = affiliateCodes.filter(code => code.IsActive).length;
  const totalUses = affiliateCodes.reduce((sum, code) => sum + (code.TotalUses || 0), 0);
  const totalCommissions = affiliateCodes.reduce((sum, code) => sum + (parseFloat(code.TotalCommissions) || 0), 0);

  if (!currentUser?.isAdmin) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto">
          <FontAwesomeIcon icon={faCode} className="text-6xl text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600">Admin privileges required to view affiliate code tracking.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
        <div className="ml-4">
          <p>Loading affiliate codes...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <MobileHeader theme={theme} />
      <div className="mobile-container">
        <div className="p-6 space-y-6">
          
          {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            <FontAwesomeIcon icon={faCode} className="mr-3" />
            Referral Code Tracking
          </h1>
          <p className="text-gray-600">Monitor and manage affiliate referral codes</p>
        </div>
      </div>

      {/* Stats Cards */}
      {showStatsCards && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faCode} className="text-blue-500 text-2xl mr-3" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{totalCodes}</div>
                <div className="text-sm text-gray-600">Total Codes</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-2xl mr-3" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{activeCodes}</div>
                <div className="text-sm text-gray-600">Active Codes</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faUsers} className="text-orange-500 text-2xl mr-3" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{totalUses}</div>
                <div className="text-sm text-gray-600">Total Uses</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faDollarSign} className="text-green-600 text-2xl mr-3" />
              <div>
                <div className="text-2xl font-bold text-gray-900">${totalCommissions.toFixed(2)}</div>
                <div className="text-sm text-gray-600">Total Commissions</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
              />
              <input
                type="text"
                placeholder="Search codes, names, emails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Affiliate</label>
            <select
              value={selectedAffiliate}
              onChange={(e) => handleAffiliateFilter(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ALL">All Affiliates</option>
              {affiliates.map(affiliate => (
                <option key={affiliate.AffiliateId} value={affiliate.AffiliateId}>
                  {affiliate.CompanyName}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => setShowStatsCards(!showStatsCards)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 mr-2"
            >
              {showStatsCards ? 'Hide Stats' : 'Show Stats'}
            </button>
          </div>
        </div>
      </div>

      {/* Codes Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Referral Codes ({filteredAndSortedCodes.length})</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('ReferralCode')}
                >
                  <div className="flex items-center">
                    Referral Code
                    <FontAwesomeIcon icon={getSortIcon('ReferralCode')} className="ml-1" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('SalespersonName')}
                >
                  <div className="flex items-center">
                    Salesperson
                    <FontAwesomeIcon icon={getSortIcon('SalespersonName')} className="ml-1" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('TotalUses')}
                >
                  <div className="flex items-center">
                    Uses
                    <FontAwesomeIcon icon={getSortIcon('TotalUses')} className="ml-1" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('TotalCommissions')}
                >
                  <div className="flex items-center">
                    Commissions
                    <FontAwesomeIcon icon={getSortIcon('TotalCommissions')} className="ml-1" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('CommissionRate')}
                >
                  <div className="flex items-center">
                    Rate
                    <FontAwesomeIcon icon={getSortIcon('CommissionRate')} className="ml-1" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('CreatedDate')}
                >
                  <div className="flex items-center">
                    Created
                    <FontAwesomeIcon icon={getSortIcon('CreatedDate')} className="ml-1" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedCodes.length > 0 ? filteredAndSortedCodes.map((code) => (
                <tr key={code.AffiliateCodeId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faCode} className="text-blue-500 mr-2" />
                      <span className="text-sm font-mono font-medium text-gray-900">
                        {code.ReferralCode}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {code.SalespersonName || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {code.SalespersonEmail || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {code.CampaignName || 'Default'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faUsers} className="text-gray-400 mr-1" />
                      {code.TotalUses || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faDollarSign} className="text-green-500 mr-1" />
                      ${(parseFloat(code.TotalCommissions) || 0).toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {((parseFloat(code.CommissionRate) || 0) * 100).toFixed(1)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      code.IsActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      <FontAwesomeIcon 
                        icon={code.IsActive ? faCheckCircle : faTimesCircle} 
                        className="mr-1" 
                      />
                      {code.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faCalendar} className="mr-1" />
                      {code.CreatedDate ? new Date(code.CreatedDate).toLocaleDateString() : 'N/A'}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    <div className="py-8">
                      <FontAwesomeIcon icon={faCode} className="text-4xl mb-3 text-gray-300" />
                      <p>No referral codes found</p>
                      <p className="text-sm">Codes will appear here as affiliates create them</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
        </div>
      </div>
    </>
  );
};

export default AdminAffiliateCodesTracking;